// react
import { type RefObject, useEffect } from 'react';

// material ui
import { useTheme } from '@mui/material/styles';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

import type HighchartsReact from 'highcharts-react-official';

/**
 * Synchronizes a Highcharts chart instance with the current MUI theme
 * and manages loading state display.
 *
 * Handles three concerns:
 * 1. Reflows the chart when the theme changes
 * 2. Applies dark/light colour palette to axes, legend, tooltip
 * 3. Shows/hides the Highcharts loading overlay
 */
const useHighchartsThemeSync = (
  chartRef: RefObject<HighchartsReact.RefObject | null>,
  isLoading?: boolean,
) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Reflow on theme change
  useEffect(() => {
    chartRef.current?.chart?.reflow();
  }, [theme, chartRef]);

  // Apply theme colours
  useEffect(() => {
    const themeOptions = isDark ? darkTheme : lightTheme;
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.update(
      {
        chart: { backgroundColor: themeOptions.chart.backgroundColor },
        title: { style: { color: themeOptions.title.style.color } },
        xAxis: {
          gridLineColor: 'transparent',
          lineColor: themeOptions.xAxis.lineColor,
          labels: { style: { color: themeOptions.xAxis.labels.style.color } },
        },
        yAxis: [
          {
            gridLineColor: 'transparent',
            labels: {
              style: { color: themeOptions.yAxis.labels.style.color },
            },
          },
        ],
        legend: {
          itemStyle: { color: themeOptions.legend.itemStyle.color },
        },
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          style: { color: isDark ? 'white' : 'black' },
        },
      },
      false,
    );
    chart.redraw();
  }, [theme, isDark, chartRef]);

  // Loading state
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    if (isLoading) {
      chart.showLoading('Loading data...');
    } else {
      chart.hideLoading();
    }
  }, [isLoading, chartRef]);

  const themeOptions = isDark ? darkTheme : lightTheme;

  return {
    chart: { backgroundColor: themeOptions.chart.backgroundColor },
    title: { style: { color: themeOptions.title.style.color } },
    xAxis: {
      gridLineColor: 'transparent',
      lineColor: themeOptions.xAxis.lineColor,
      labels: { style: { color: themeOptions.xAxis.labels.style.color } },
    },
    yAxis: [
      {
        gridLineColor: 'transparent',
        labels: {
          style: { color: themeOptions.yAxis.labels.style.color },
        },
      },
    ],
    legend: {
      itemStyle: { color: themeOptions.legend.itemStyle.color },
    },
    tooltip: {
      backgroundColor: themeOptions.tooltip.backgroundColor,
      style: { color: isDark ? 'white' : 'black' },
    },
  };
};

export default useHighchartsThemeSync;
